/**
 * Change-since-last-run diff - pure frontend, computed from two RunSnapshots.
 *
 * Given the two newest snapshots for a competitor (current + previous), produce
 * a compact "what changed" summary: the threat-score delta, the level change,
 * and which top-threats are new vs resolved. No backend involvement - this is
 * derived entirely from the fetched history.
 */

import type { RunSnapshot } from "./agentcore";

export interface RunDiff {
  /** current.score - previous.score (positive = threat rose). */
  scoreDelta: number;
  /** "up" | "down" | "same" - direction of the score change. */
  direction: "up" | "down" | "same";
  /** Whether the threat LEVEL changed (e.g. MEDIUM → HIGH). */
  levelChanged: boolean;
  previousLevel: RunSnapshot["level"];
  currentLevel: RunSnapshot["level"];
  /** Threats present now but not in the previous run. */
  newThreats: string[];
  /** Threats present previously but gone now (resolved / no longer surfaced). */
  resolvedThreats: string[];
  /** Confidence delta when both runs reported it, else null. */
  confidenceDelta: number | null;
  /** ISO timestamps for the two compared runs. */
  currentRunAt: string;
  previousRunAt: string;
}

/** Normalise a threat string for set comparison (case/whitespace-insensitive). */
function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Compute a diff from a competitor's snapshots. Pass them in ANY order - the two
 * NEWEST by runAt are selected and compared (newest = "current"). Returns null
 * when fewer than two comparable snapshots exist.
 */
export function computeRunDiff(snapshots: RunSnapshot[]): RunDiff | null {
  if (!Array.isArray(snapshots) || snapshots.length < 2) return null;
  const sorted = [...snapshots].sort(
    (a, b) => Date.parse(b.runAt) - Date.parse(a.runAt),
  );
  const current = sorted[0];
  const previous = sorted[1];
  if (!current || !previous) return null;

  const scoreDelta = current.score - previous.score;
  const direction: RunDiff["direction"] =
    scoreDelta > 0 ? "up" : scoreDelta < 0 ? "down" : "same";

  const prevSet = new Set(previous.topThreats.map(norm));
  const currSet = new Set(current.topThreats.map(norm));

  const newThreats = current.topThreats.filter((t) => !prevSet.has(norm(t)));
  const resolvedThreats = previous.topThreats.filter((t) => !currSet.has(norm(t)));

  const confidenceDelta =
    typeof current.confidence === "number" && typeof previous.confidence === "number"
      ? current.confidence - previous.confidence
      : null;

  return {
    scoreDelta,
    direction,
    levelChanged: current.level !== previous.level,
    previousLevel: previous.level,
    currentLevel: current.level,
    newThreats,
    resolvedThreats,
    confidenceDelta,
    currentRunAt: current.runAt,
    previousRunAt: previous.runAt,
  };
}
